/**
 * Config-specific mutations for Transfer and Export requests
 * These mutations are used by the configurable request forms (DFE config pages)
 * Split from main mutations file for tree-shaking optimization
 */
import { graphql } from "../generated"

// Transfer Request Mutations

export const saveTransferRequestMutation = graphql(`
  mutation saveTransferRequestMutation($input: CoreTransferRequestInput!) {
    saveTransferRequest(input: $input) {
      id
      version
      status {
        code
        label
      }
      certificateNumber
      products {
        id
      }
    }
  }
`)

export const raiseTransferRequest = graphql(`
  mutation RaiseTransferRequest($id: ID!, $version: Int, $validation: Boolean) {
    raiseTransferRequest(
      input: { requestID: $id, version: $version, validationOnly: $validation }
    ) {
      certificateNumber
      id
      status
      submittedAt
      warnings {
        id
        errorType
        errorMsg
        message
        field
        fieldFuid
        additionalFuid
        maxLength
        fieldValue
        additionalFieldValue
        type
        details
      }
    }
  }
`)

export const ChangeTransferRequestStatusMutation = graphql(`
  mutation changeTransferRequestStatusMutation(
    $input: ChangeTransferRequestStatusInput!
  ) {
    changeTransferRequestStatus(input: $input) {
      transferRequestId
      status
      version
    }
  }
`)

export const mutateDeleteTransferRequestById = graphql(`
  mutation deleteTransferRequestById($id: ID!) {
    deleteTransferRequestById(id: $id)
  }
`)

export const updateApprovedTransferTransportMutation = graphql(`
  mutation updateApprovedTransferTransport(
    $input: UpdateApprovedTransferTransportInput!
  ) {
    updateApprovedTransferTransport(input: $input)
  }
`)

export const downloadTransferRequestsAsCsvMutation = graphql(`
  mutation downloadTransferRequestsAsCsv(
    $input: DownloadTransferRequestsCsvInput!
  ) {
    downloadTransferRequestsAsCsv(input: $input) {
      filename
      presignedUrl
    }
  }
`)

export const downloadStatusReportMutation = graphql(`
  mutation downloadStatusReport($input: DownloadStatusReportInput!) {
    downloadStatusReport(input: $input) {
      filename
      presignedUrl
    }
  }
`)

// Export Request Mutations

export const saveExportRequestMutation = graphql(`
  mutation saveExportRequestMutation($input: CoreExportRequestInput!) {
    saveExportRequest(input: $input) {
      id
      version
      status {
        label
      }
    }
  }
`)

export const raiseExportRequest = graphql(`
  mutation RaiseExportRequest($id: ID!, $version: Int, $validation: Boolean) {
    raiseExportRequest(
      input: { requestID: $id, version: $version, validationOnly: $validation }
    ) {
      certificateNumber
      id
      status
      submittedAt
      warnings {
        id
        errorType
        errorMsg
        message
        field
        fieldFuid
        additionalFuid
        maxLength
        fieldValue
        additionalFieldValue
        type
        details
      }
    }
  }
`)

export const ChangeExportRequestStatusMutation = graphql(`
  mutation changeExportRequestStatusMutation(
    $input: ChangeExportRequestStatusInput!
  ) {
    changeExportRequestStatus(input: $input) {
      exportRequestId
      status
      version
    }
  }
`)

export const mutateDeleteExportRequestById = graphql(`
  mutation deleteExportRequestById($id: ID!) {
    deleteExportRequestById(id: $id)
  }
`)

export const deleteReplacementExportRequestMutation = graphql(`
  mutation deleteReplacementExportRequest(
    $input: DeleteReplacementExportRequestInput!
  ) {
    deleteReplacementExportRequest(input: $input) {
      parentId
    }
  }
`)

export const generateExportPdfMutation = graphql(`
  mutation generateExportPdf($input: PrintCertificateInput!) {
    printCertificate(input: $input) {
      executionArn
      exportRequestId
      pdfCertificateUrl
      exportRequestOutputId
    }
  }
`)

export const updatePrintReasonMutation = graphql(`
  mutation updatePrintReason($input: UpdatePrintReasonInput!) {
    updatePrintReason(input: $input) {
      outputEventId
      exportRequestOutputId
      reasonForReprinting
      createdAt
    }
  }
`)

// Referenced Document Mutations (used by config forms

export const newCoreReferencedDocumentMutation = graphql(`
  mutation newCoreReferencedDocument($input: NewCoreReferencedDocumentInput!) {
    newCoreReferencedDocument(input: $input) {
      id
      url
    }
  }
`)

export const deleteCoreReferencedDocumentMutation = graphql(`
  mutation deleteCoreReferencedDocument($id: ID!) {
    deleteCoreReferencedDocument(id: $id)
  }
`)

// Supporting Document Mutations

export const createSupportingDocumentMutation = graphql(`
  mutation createSupportingDocument($input: CoreSupportingDocumentInput!) {
    createSupportingDocument(input: $input) {
      id
      comment
      documentNumber
      documentReferenceNumber
      primaryOperator {
        id
        name
      }
      intendedOperators {
        id
        name
      }
      originCountry {
        id
        name
      }
      productDescription
      identificationMarks
      foreignManufacturePremises
      foreignPremisesId
      nzManufacturePremises
      productionDates {
        from
        to
      }
    }
  }
`)

export const cancelSupportingDocumentMutation = graphql(`
  mutation CancelSupportingDocument($input: CancelSupportingDocumentInput!) {
    cancelSupportingDocument(input: $input) {
      success
      message
      supportingDocument {
        id
        status
      }
    }
  }
`)

export const updateSupportingDocumentMutation = graphql(`
  mutation UpdateSupportingDocument($input: UpdateSupportingDocumentInput!) {
    updateSupportingDocument(input: $input) {
      success
      message
      supportingDocument {
        id
        status
        documentNumber
        documentReferenceNumber
        primaryOperator {
          id
          name
        }
        intendedOperators {
          id
          name
        }
        productDescription
        identificationMarks
        foreignManufacturePremises
        foreignPremisesId
        nzManufacturePremises
        productionDates {
          from
          to
        }
        originCountry {
          id
          name
        }
      }
    }
  }
`)

export const extendIntendedOperatorMutation = graphql(`
  mutation ExtendIntendedOperator($input: ExtendIntendedOperatorInput!) {
    extendIntendedOperator(input: $input) {
      success
      message
      supportingDocument {
        id
        status
        intendedOperators {
          id
          name
        }
      }
    }
  }
`)

export const downloadSupportingDocumentsAsCsvMutation = graphql(`
  mutation downloadSupportingDocumentsAsCsv(
    $input: DownloadSupportingDocumentsCsvInput!
  ) {
    downloadSupportingDocumentsAsCsv(input: $input) {
      filename
      presignedUrl
    }
  }
`)

// Exhaust Transfer Products Mutation

export const exhaustTransferProductsMutation = graphql(`
  mutation exhaustTransferProducts($input: ExhaustTransferProductsInput!) {
    exhaustTransferProducts(input: $input) {
      results {
        transferRequestId
        products {
          id
          exhausted
        }
        isFullyExhausted
      }
    }
  }
`)
